import React from 'react';
import { object, func, bool } from 'prop-types';
import _ from 'lodash';
import ButtonMenu from '../../global/button-menu';
import { Checkbox } from '~/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import './_project-filters.scss';

const ProjectsFilters = ({ filters, onFiltersChange, inline = false }) => {
	// CATEGORY FILTER
	const handleCategoryChange = (value) => {
		const { category } = filters;
		const selected = category.options?.find((item) => item.label === value);

		// Ignore the set if the filter is already set to the provided category
		// (avoids redundant listener calls)
		if(selected.value === category.selected) {
			return;
		}

		onFiltersChange('category', selected.value);
	};

	const resetCategoryDefault = () => {
		const { category } = filters;
		onFiltersChange('category', category.defaultValue);
	};

	const isCategorySelected = (category) => {
		return _.isEqual(filters.category.selected, category);
	};

	const renderCategoryFilter = () => {
		const { category } = filters;
		const categoryLabel = category.selected.archived ? 'Archived' : 'Active';

		const content = (
			<div className="pf-category-menu">
				<RadioGroup
					value={category.options.find((option) => isCategorySelected(option.value))?.label}
					onValueChange={handleCategoryChange}
				>
					{category.options.map((option, key) => (
						<label
							htmlFor={`projectsCategory_${key}`}
							key={option.label}
							data-cy="category-filter"
						>
							<RadioGroupItem
								id={`projectsCategory_${key}`}
								value={option.label}
							/>
							<span>{option.label}</span>
						</label>
					))}
				</RadioGroup>
			</div>
		);

		if( inline ) {
			return (
				<div className="pf-inline-section">
					<h4>Status</h4>
					<RadioGroup
						value={category.options.find((option) => isCategorySelected(option.value))?.label}
						onValueChange={handleCategoryChange}
					>
						<div className="grid grid-cols-2 gap-3">
							{category.options.map((option, key) => {
								const selected = isCategorySelected(option.value);

								return (
									<label
										htmlFor={`projectsCategory_${key}`}
										key={option.label}
										data-cy="category-filter"
										className="block cursor-pointer"
									>
										<RadioGroupItem
											id={`projectsCategory_${key}`}
											value={option.label}
											className="sr-only"
										/>
										<span
											className={[
												'flex items-center justify-center rounded-md px-4 py-3 text-white text-sm font-semibold transition-colors duration-200',
												selected
													? 'bg-primary border border-primary'
													: 'bg-secondary border border-border'
											].join(' ')}
											style={{ minHeight: '2.75rem' }}
										>{option.label}</span>
									</label>
								);
							})}
						</div>
					</RadioGroup>
				</div>
			);
		}

		return (
			<ButtonMenu
				label={categoryLabel}
				isDirty={true}
				onResetClick={resetCategoryDefault}
				cyName="projects-category"
			>
				{content}
			</ButtonMenu>
		);
	};

	// STUDIOS FILTER
	const handleStudiosChange = (e) => {
		const { value } = e.target;
		const { studios } = filters;
		const selectedOption = studios.options.find((option) => option.value === value);

		if(selectedOption && selectedOption.count === 0) {
			return;
		}

		const nextSelected = [...studios.selected];

		let idx = nextSelected.indexOf(value);
		if(idx === -1) {
			nextSelected.push(value);
		}
		else {
			nextSelected.splice(idx, 1);
		}

		onFiltersChange('studios', nextSelected);
	};

	const resetStudiosDefault = () => {
		const { studios } = filters;
		onFiltersChange('studios', studios.defaultValue);
	};

	const isStudioSelected = (value) => {
		const { studios } = filters;
		return studios.selected.includes(value);
	};

	const isStudiosModified = () => {
		const { studios } = filters;
		return studios.selected.length > 0;
	};

	const renderStudiosFilter = () => {
		const { studios } = filters;
		const studiosLength = studios.selected.length;
		const studiosLabel = studiosLength === 0
			? 'Studio'
			: `${studiosLength} Studio${studiosLength > 1 ? 's' : ''} Selected`;

		const isTwoCol = studios.options.length > 7;

		const content = (
			<div
				className={isTwoCol ? 'pf-studio-menu pf-studio-menu--two-col' : 'pf-studio-menu'}
			>
				{studios.options.map((option, key) => (
					<label
						htmlFor={`projectsStudio_${key}`}
						key={option.value}
						data-cy="studio-filter"
						data-disabled={option.count === 0}
					>
						<Checkbox
							id={`projectsStudio_${key}`}
							checked={isStudioSelected(option.value)}
							disabled={option.count === 0}
							onCheckedChange={() => handleStudiosChange({ target: { value: option.value } })}
						/>
						<span>{`${option.label} (${option.count})`}</span>
					</label>
				))}
			</div>
		);

		if( inline ) {
			return (
				<div className="pf-inline-section">
					<h4>Studios</h4>
					<div className="flex flex-wrap gap-3">
						{studios.options.map((option, key) => {
							const selected = isStudioSelected(option.value);
							const disabled = option.count === 0;

							return (
								<label
									htmlFor={`projectsStudio_${key}`}
									key={option.value}
									data-cy="studio-filter"
									className={disabled ? 'block cursor-not-allowed' : 'block cursor-pointer'}
									style={disabled ? { opacity: 0.45 } : undefined}
								>
									<Checkbox
										id={`projectsStudio_${key}`}
										checked={selected}
										disabled={disabled}
										onCheckedChange={() => handleStudiosChange({ target: { value: option.value } })}
										className="sr-only"
									/>
									<span
										className={[
											'inline-flex items-center justify-center rounded-full px-4 py-2 text-white text-sm leading-tight transition-colors duration-200',
											selected ? 'bg-primary border border-primary font-bold' : 'bg-secondary border border-border font-medium'
										].join(' ')}
										style={{ minHeight: '2.5rem' }}
									>
										{`${option.label} (${option.count})`}
									</span>
								</label>
							);
						})}
					</div>
				</div>
			);
		}

		return (
			<ButtonMenu
				label={studiosLabel}
				isDirty={isStudiosModified()}
				onResetClick={resetStudiosDefault}
				cyName="projects-studios"
			>
				{content}
			</ButtonMenu>
		);
	};

	return (
		inline
			? <div className="flex flex-col gap-6">
				{renderCategoryFilter()}
				{renderStudiosFilter()}
			</div>
			: <div className="flex gap-1">
				{renderCategoryFilter()}
				{renderStudiosFilter()}
			</div>
	);
};

ProjectsFilters.propTypes = {
	filters: object.isRequired,
	onFiltersChange: func.isRequired,
	inline: bool
};

export default ProjectsFilters;
